import * as React from "react"
import { Navigate } from "react-router"

import type { Role } from "@/lib/roles"

import { useCurrentUser } from "./auth-store"

/**
 * Role guard for a single route. Renders `children` when the signed-in user's role passes
 * `allow`; otherwise sends them to the queue, the one screen every role can open.
 *
 * Mount it inside `RequireAuth` (the shell already is), so a missing session is handled
 * there and bounced to /login with a redirect — this guard only answers "may this
 * account see this screen", never "is anyone signed in".
 *
 * Like `RequireAuth`, this is display-only: the backend refuses the screen's requests
 * with a 403 regardless. The guard stops a non-admin from landing on a page that can only
 * show them errors.
 */
export function RequireRole({
  allow,
  children,
}: {
  allow: (role: Role | null) => boolean
  children: React.ReactNode
}): JSX.Element {
  const user = useCurrentUser()

  if (!allow(user?.role ?? null)) return <Navigate to="/" replace />

  return <>{children}</>
}
